import useMarket from 'stores/useMarket';
import { useLoadMore, useProductsContext, useUpdateProducts } from 'stores/useProducts';
import { useGetProductsQuery } from 'generated/graphql';
import React, { ReactNode, useEffect } from 'react';
import { useOrganisationPersonalWebshop } from './useOrganisationPersonalWebshop';
import { useServerTranslation } from './useServerTranslation';

type Props = {
    slug?: string;
    searchValue?: string;
    options?: {
        tag?: string;
        includeVariants?: boolean;
    };
};

function itemToArray<T>(item?: T): T[] {
    return [item].filter(Boolean);
}

export default function useGetProducts({
    slug,
    searchValue,
    options,
}: Props) {
    const from = useProductsContext((store) => store.from);
    const filters = useProductsContext((store) => store.selectedFilters);
    const products = useProductsContext((store) => store.products);
    const sort = useProductsContext((store) => store.sorting);
    const market = useMarket((store) => store.market);

    const [message, setMessage] = React.useState<ReactNode>(undefined);
    const { organisationPersonalWebshopSlug } = useOrganisationPersonalWebshop();

    const { t } = useServerTranslation();

    const { data, loading, error } = useGetProductsQuery({
        variables:
        {
            market,
            from,
            sort,
            size: 24,
            category_slug_path: slug,
            filters,
            searchField: searchValue,
            organisationPersonalWebshopSlug,
            tags: itemToArray(options?.tag),
            includeVariants: options?.includeVariants,
        },
    });

    useUpdateProducts(data?.products ?? undefined, slug);

    const loadMore = useLoadMore();

    useEffect(() => {
        if (!products) {
            if (error) {
                setMessage(<h2>{t('fetch-messages.error')}</h2>);
            }
            if (loading) {
                setMessage(<h2>{t('fetch-messages.loading')}</h2>);
            }
        } else {
            setMessage(undefined);
        }
    }, [products, error, loading, t]);

    return {
        products,
        message,
        loading,
        loadMore,
    };
}
